import React from 'react';
import { Box, Flex, Grid, Text } from '@chakra-ui/react';
import { MarkWithBrandProps } from '../../../modules/catalog/types';
import { useAppSelector } from '../../../utils/reduxHooks';
import { selectMarksList, selectSelectedCarType } from '../../../modules/catalog/selectors';
import { generatePath, useNavigate } from 'react-router-dom';
import { routes } from '../../../constants/routes';

export const MarksGrid: React.FC = () => {
  const navigate = useNavigate();
  const marks = useAppSelector(selectMarksList);
  const carType = useAppSelector(selectSelectedCarType);

  if (!marks) return null;

  const filteredMarks = carType ? marks.filter(mark => mark.brandId === carType) : marks;

  const sortedMarks = [...filteredMarks].sort((a, b) => a.name.localeCompare(b.name));

  // Разделяем на английские и русские группы
  const isEnglishLetter = (letter: string) => /[A-Za-z]/.test(letter);

  const groupedMarks = sortedMarks.reduce<{
    english: Record<string, MarkWithBrandProps[]>;
    russian: Record<string, MarkWithBrandProps[]>;
  }>(
    (acc, mark) => {
      const firstLetter = mark.name.charAt(0).toUpperCase();
      const group = isEnglishLetter(firstLetter) ? 'english' : 'russian';
      if (!acc[group][firstLetter]) {
        acc[group][firstLetter] = [];
      }
      acc[group][firstLetter].push(mark as MarkWithBrandProps);
      return acc;
    },
    { english: {}, russian: {} },
  );

  // Разбиваем группы на массивы по 5 букв в каждой колонке
  const splitGroups = (group: Record<string, MarkWithBrandProps[]>) =>
    Object.entries(group).reduce<{ letter: string; marks: MarkWithBrandProps[] }[][]>(
      (acc, [letter, marks], index) => {
        const columnIndex = Math.floor(index / 5);
        if (!acc[columnIndex]) {
          acc[columnIndex] = [];
        }
        acc[columnIndex].push({ letter, marks });
        return acc;
      },
      [],
    );

  const englishGroups = splitGroups(groupedMarks.english);
  const russianGroups = splitGroups(groupedMarks.russian);

  const handleMarkSelect = (mark: MarkWithBrandProps) => {
    navigate(generatePath(routes.CATALOG_MODEL, { type: mark.brandId, mark: mark.id }));
  };

  return (
    <Box>
      {englishGroups.length ? (
        <>
          <Text fontWeight="bold" fontSize="2xl" mb={4}>
            A-Z
          </Text>
          <Grid templateColumns="repeat(4, 1fr)" gap={6} mb={10}>
            {englishGroups.map((group, groupIndex) => (
              <Box key={groupIndex}>
                {group.map(({ letter, marks }) => (
                  <Flex key={letter} mb={10}>
                    <Text paddingRight="12px" fontWeight="bold" fontSize="xl" mb={2}>
                      {letter}
                    </Text>
                    <Flex direction="column">
                      {marks.map((mark, index) => (
                        <Box
                          key={index}
                          onClick={() => handleMarkSelect(mark)}
                          cursor="pointer"
                          mb={1}
                        >
                          <Text>{mark.name}</Text>
                        </Box>
                      ))}
                    </Flex>
                  </Flex>
                ))}
              </Box>
            ))}
          </Grid>
        </>
      ) : null}

      {russianGroups.length ? (
        <>
          <Text fontWeight="bold" fontSize="2xl" mb={4}>
            А-Я
          </Text>
          <Grid templateColumns="repeat(4, 1fr)" gap={10} mb={10}>
            {russianGroups.map((group, groupIndex) => (
              <Box key={groupIndex}>
                {group.map(({ letter, marks }, index: number) => (
                  <Flex key={index} mb={10}>
                    <Text paddingRight="12px" fontWeight="bold" fontSize="xl" mb={2}>
                      {letter}
                    </Text>
                    <Flex direction="column">
                      {marks.map((mark, index) => (
                        <Box
                          key={index}
                          onClick={() => handleMarkSelect(mark)}
                          cursor="pointer"
                          mb={1}
                        >
                          <Text>{mark.name}</Text>
                        </Box>
                      ))}
                    </Flex>
                  </Flex>
                ))}
              </Box>
            ))}
          </Grid>
        </>
      ) : null}
    </Box>
  );
};
